import { useState, useEffect, useCallback } from 'react';

// Two series, one meaning each: the order is still standing, or it was
// cancelled. Green would be the intuitive choice for "alive" but green-vs-red is
// the classic colour-blind failure — it measures ΔE 8.1 under deuteranopia,
// below the safe threshold. Blue-vs-red measures 26.7, and blue is already this
// dashboard's "ordered" colour.
const STANDING  = '#3b82f6';
const CANCELLED = '#ef4444';

const FALLBACK_WINDOWS = [
  { key: '24h', label: 'Last 24 hours' },
  { key: '72h', label: 'Last 72 hours' },
  { key: '7d',  label: 'Last 7 days'   },
  { key: '30d', label: 'Last 30 days'  },
  { key: 'all', label: 'All stored'    },
];

function fmtRate(rate) {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(rate >= 0.1 ? 0 : 1)}%`;
}

// Survival runs from minutes to days, so the unit follows the magnitude rather
// than forcing everything into one ("0.38h" and "72h" both read as noise).
function fmtDur(mins) {
  if (mins === null || mins === undefined) return '—';
  // A decimal is kept under an hour on purpose. These cancellations cluster in a
  // band about one minute wide, so rounding 22.8 to "23m" would hide the very
  // thing the number is here to show — that the timing is machine-tight.
  if (mins < 60)   return `${mins.toFixed(1)}m`;
  if (mins < 120)  return `${Math.round(mins)}m`;
  if (mins < 2880) return `${(mins / 60).toFixed(1)}h`;
  return `${(mins / 1440).toFixed(1)}d`;
}

function fmtWhen(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function rateColor(rate) {
  if (rate === null || rate === undefined) return '#64748b';
  if (rate >= 0.25) return '#f87171';
  if (rate >= 0.08) return '#fbbf24';
  return '#34d399';
}

function KpiCard({ label, value, sub, accent, isMobile, title }) {
  return (
    <div title={title} style={{
      background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 12, padding: isMobile ? '12px 14px' : '16px 18px',
      flex: 1, minWidth: isMobile ? 130 : 160,
    }}>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: accent || '#f1f5f9', marginTop: 4, letterSpacing: '-0.5px' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Pills({ options, value, onChange, prefix }) {
  return (
    <div style={{
      display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center',
      background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 10, padding: 4,
    }}>
      {prefix && <span style={{ fontSize: 11, color: '#475569', padding: '0 8px', fontWeight: 500 }}>{prefix}</span>}
      {options.map(o => {
        const active = value === o.key;
        return (
          <button key={o.key} onClick={() => onChange(o.key)} style={{
            fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 7,
            border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
            background: active ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'transparent',
            color: active ? '#fff' : '#64748b',
            boxShadow: active ? '0 2px 8px rgba(99,102,241,0.35)' : 'none',
            transition: 'all 0.12s',
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
      {items.map(i => (
        <span key={i.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8' }}>
          <span style={{ width: 9, height: 9, borderRadius: 3, background: i.color, flexShrink: 0 }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

function Card({ title, note, children, isMobile }) {
  return (
    <div style={{
      background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        padding: isMobile ? '11px 12px' : '13px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>{title}</span>
        {note && <span style={{ fontSize: 11, color: '#475569', textAlign: 'right' }}>{note}</span>}
      </div>
      {children}
    </div>
  );
}

// Each day's orders, split by what became of them. Stacked rather than paired
// because the two parts sum to that day's placements — the whole bar is the
// cohort and the red portion is how much of it died.
function Timeline({ series, isMobile }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...series.map(d => d.placed));
  const H = isMobile ? 80 : 110;
  const active = hover === null ? null : series[hover];
  const label = iso => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  return (
    <div style={{
      background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 12, padding: isMobile ? '12px' : '14px 16px', marginBottom: isMobile ? 12 : 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>Orders placed per day, by outcome</span>
        <Legend items={[{ label: 'Still standing', color: STANDING }, { label: 'Cancelled', color: CANCELLED }]} />
      </div>

      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute', top: -4, left: 0, right: 0, height: 20,
          display: 'flex', justifyContent: 'center', pointerEvents: 'none',
          opacity: active ? 1 : 0, transition: 'opacity 0.12s',
        }}>
          {active && (
            <span style={{
              fontSize: 11, color: '#e2e8f0', background: '#0f172a',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7,
              padding: '3px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
              boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
            }}>
              {label(active.date)} · {active.placed.toLocaleString()} placed ·{' '}
              <span style={{ color: CANCELLED, fontWeight: 600 }}>{active.cancelled.toLocaleString()} cancelled</span>
              {' · '}<span style={{ color: STANDING, fontWeight: 600 }}>{active.standing.toLocaleString()} standing</span>
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: H, marginTop: 20 }}>
          {series.map((d, i) => {
            const total = (d.placed / max) * 100;
            const cancelShare = d.placed ? d.cancelled / d.placed : 0;
            return (
              <div
                key={d.date}
                data-day={d.date}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(h => (h === i ? null : h))}
                onClick={() => setHover(h => (h === i ? null : i))}
                style={{
                  flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end',
                  justifyContent: 'center', minWidth: 0, cursor: 'pointer',
                  background: hover === i ? 'rgba(255,255,255,0.05)' : 'transparent',
                  borderRadius: 4, transition: 'background 0.12s',
                }}
              >
                <div style={{
                  width: '68%', maxWidth: 14, height: d.placed ? `${Math.max(3, total)}%` : 2,
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                  background: d.placed ? 'transparent' : 'rgba(255,255,255,0.07)',
                  borderRadius: '4px 4px 0 0', overflow: 'hidden',
                }}>
                  {d.placed > 0 && (
                    <>
                      <div style={{ flex: d.standing, background: STANDING, borderRadius: '4px 4px 0 0', minHeight: d.standing ? 2 : 0 }} />
                      {/* 2px of surface between the segments so the boundary is
                          a gap rather than two colours touching. */}
                      {d.standing > 0 && d.cancelled > 0 && <div style={{ height: 2, flexShrink: 0 }} />}
                      <div style={{ flex: d.cancelled, background: CANCELLED, minHeight: d.cancelled ? 2 : 0,
                                    borderRadius: cancelShare === 1 ? '4px 4px 0 0' : 0 }} />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#475569', marginTop: 6 }}>
        <span>{label(series[0].date)}</span>
        <span style={{ color: '#334155' }}>peak {max.toLocaleString()}/day</span>
        <span>{label(series[series.length - 1].date)}</span>
      </div>
    </div>
  );
}

function BarRow({ label, count, max, color, suffix, labelWidth = 92 }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: `${labelWidth}px 1fr auto`,
      gap: 10, alignItems: 'center', padding: '5px 0',
    }}>
      <span style={{ fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${(count / max) * 100}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s ease-out' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: count ? '#e2e8f0' : '#475569', textAlign: 'right', fontVariantNumeric: 'tabular-nums', minWidth: 46 }}>
        {count.toLocaleString()}{suffix}
      </span>
    </div>
  );
}

const dimCols = m => m ? '1fr 44px 44px 46px' : '1fr 60px 62px 62px 74px';

function DimensionCard({ dimension, isMobile }) {
  return (
    <Card title={`By ${dimension.label}`} note="most cancelled first" isMobile={isMobile}>
      <div style={{
        display: 'grid', gridTemplateColumns: dimCols(isMobile), gap: isMobile ? 8 : 12,
        padding: isMobile ? '7px 10px' : '7px 14px',
        fontSize: 10, color: '#475569', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.5px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        <span>{dimension.label}</span>
        <span style={{ textAlign: 'right' }}>Placed</span>
        <span style={{ textAlign: 'right', color: '#93c5fd' }}>Standing</span>
        <span style={{ textAlign: 'right' }}>Cancelled</span>
        {!isMobile && <span style={{ textAlign: 'right' }}>Survived</span>}
      </div>

      {dimension.groups.map(g => (
        <div key={g.key} style={{
          display: 'grid', gridTemplateColumns: dimCols(isMobile), gap: isMobile ? 8 : 12,
          alignItems: 'center', padding: isMobile ? '8px 10px' : '9px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 500, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.key}>
            {g.key}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{g.placed.toLocaleString()}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: g.standing ? STANDING : '#475569', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{g.standing.toLocaleString()}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: g.cancelled ? CANCELLED : '#475569', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {g.cancelled.toLocaleString()}
            <span style={{ color: '#475569', fontWeight: 400, fontSize: 10 }}> {fmtRate(g.cancelRate)}</span>
          </div>
          {!isMobile && (
            <div style={{ fontSize: 12, color: '#fbbf24', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                 title={g.survivalSamples ? `median of ${g.survivalSamples} cancelled orders` : 'nothing cancelled'}>
              {fmtDur(g.medianSurvivalMinutes)}
            </div>
          )}
        </div>
      ))}
      {!dimension.groups.length && (
        <div style={{ padding: 20, fontSize: 12, color: '#475569', textAlign: 'center' }}>Nothing in this window</div>
      )}
    </Card>
  );
}

const rowCols = m => m ? '1fr 62px' : '1fr 150px 96px 132px 66px';

function OrderRow({ row, isMobile }) {
  const [open, setOpen] = useState(false);
  const cancels = row.cancels || [];

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
      <div
        onClick={() => cancels.length && setOpen(o => !o)}
        style={{
          display: 'grid', gridTemplateColumns: rowCols(isMobile), gap: isMobile ? 8 : 12,
          alignItems: 'center', padding: isMobile ? '9px 12px' : '10px 16px',
          cursor: cancels.length ? 'pointer' : 'default',
          background: open ? 'rgba(255,255,255,0.03)' : 'transparent',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.productName || '—'}
            {row.itemCount > 1 && (
              <span style={{ color: '#64748b', marginLeft: 6 }}>+{row.itemCount - 1} more</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.recipient}
          </div>
        </div>

        {!isMobile && (
          <div style={{ fontSize: 11, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{fmtWhen(row.placedAt)}</div>
        )}
        {!isMobile && (
          <div style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', color: row.cancelled ? CANCELLED : '#475569' }}>
            {row.cancelled
              ? `${row.cancelCount} cancel${row.cancelCount === 1 ? '' : 's'}`
              : 'none'}
          </div>
        )}
        {!isMobile && (
          <div style={{ fontSize: 11, color: row.cancelled ? '#fca5a5' : '#64748b', fontVariantNumeric: 'tabular-nums' }}>
            {row.cancelled ? fmtWhen(row.firstCancelAt) : 'still standing'}
          </div>
        )}

        <div style={{
          fontSize: 11, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
          color: row.cancelled ? '#fbbf24' : STANDING,
        }} title={row.cancelled ? 'Placed → first cancellation' : 'How long it has stood so far'}>
          {fmtDur(row.survivedMinutes)}{row.cancelled ? '' : '+'}
        </div>
      </div>

      {open && (
        <div style={{
          padding: isMobile ? '4px 12px 10px' : '4px 16px 12px 32px',
          background: 'rgba(0,0,0,0.2)', animation: 'fadeIn 0.15s ease-out',
        }}>
          <div style={{ fontSize: 10, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 0' }}>
            {cancels.length} cancellation email{cancels.length === 1 ? '' : 's'} — one per item
          </div>
          {cancels.map(c => (
            <div key={c.orderId} style={{ display: 'flex', gap: 12, padding: '3px 0', fontSize: 11, color: '#94a3b8' }}>
              <span style={{ fontFamily: '"SF Mono", "Fira Code", monospace', color: '#cbd5e1' }}>{c.orderId}</span>
              <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{fmtWhen(c.at)}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: '#475569', paddingTop: 6 }}>{row.subject}</div>
        </div>
      )}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function OrderTracking({ isMobile, tracing, onTrace }) {
  const [win, setWin]   = useState('7d');
  const [list, setList] = useState('cancelled');   // cancelled | standing
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/order-tracking?window=${win}`);
      if (!res.ok) throw new Error('Could not load order tracking');
      setData(await res.json());
      setError(null);
    } catch (e) { setError(e.message); }
  }, [win]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tracing === false) load(); }, [tracing, load]);

  if (error) return <div style={{ padding: 30, textAlign: 'center', color: '#f87171', fontSize: 13 }}>⚠ {error}</div>;
  if (!data)  return <div style={{ padding: 30, textAlign: 'center', color: '#475569', fontSize: 13 }}>Loading order tracking…</div>;

  // Nothing has ever been traced. Distinct from "traced, found nothing" — say
  // which, rather than showing a wall of zeros that looks like a quiet week.
  if (!data.lastTrace) {
    return (
      <div style={{ padding: '44px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 30, marginBottom: 10 }}>🚫</div>
        <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 600, marginBottom: 6 }}>No orders traced yet</div>
        <div style={{ fontSize: 12, color: '#64748b', maxWidth: 460, margin: '0 auto', lineHeight: 1.6 }}>
          The hourly sync fills this in on its own. To populate it now — or to pull a longer stretch of history
          in one go — pick a range and run a trace from the toolbar above.
        </div>
        {onTrace && (
          <button
            onClick={() => onTrace()}
            disabled={!!tracing}
            style={{
              marginTop: 16, background: tracing ? '#1e293b' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: tracing ? '#475569' : '#fff', border: 'none', borderRadius: 10,
              padding: '9px 18px', fontSize: 13, fontWeight: 600,
              cursor: tracing ? 'wait' : 'pointer',
            }}
          >
            {tracing ? 'Tracing…' : 'Run a trace'}
          </button>
        )}
      </div>
    );
  }

  const { overall: o, buckets, timeline, dimensions, products, cancelEmailsPerCheckout } = data;
  const windows  = data.windows?.length ? data.windows : FALLBACK_WINDOWS;
  const winLabel = (data.window?.label || '').toLowerCase();

  const rows      = list === 'standing' ? data.standingOrders : data.cancellations;
  const rowsTotal = list === 'standing' ? data.standingTotal  : data.cancellationsTotal;
  const shown     = showAll ? rows : rows.slice(0, 25);

  const maxBucket  = Math.max(1, ...buckets.map(b => b.count));
  const maxPerCk   = Math.max(1, ...cancelEmailsPerCheckout.map(c => c.checkouts));
  const maxProduct = Math.max(1, ...products.map(p => p.placed));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: isMobile ? 12 : 16, alignItems: 'center' }}>
        <Pills options={windows} value={win} onChange={setWin} prefix="Placed in" />
        {data.lastTrace && (
          <span style={{ fontSize: 11, color: '#475569' }}>traced {fmtWhen(data.lastTrace)}</span>
        )}
      </div>

      {/* ── KPI row ─────────────────────────────────── */}
      <div style={{ display: 'flex', gap: isMobile ? 8 : 12, flexWrap: 'wrap', marginBottom: isMobile ? 14 : 20 }}>
        <KpiCard
          label="Still standing"
          value={o.standing.toLocaleString()}
          sub={o.standingPastDanger !== null
            ? `${o.standingPastDanger.toLocaleString()} past the usual cancel time`
            : `of ${o.placed.toLocaleString()} placed`}
          accent={STANDING}
          isMobile={isMobile}
          title="Orders placed in this window with no cancellation email yet"
        />
        <KpiCard
          label="Cancelled"
          value={o.cancelled.toLocaleString()}
          sub={`${fmtRate(o.cancelRate)} of ${o.placed.toLocaleString()} placed`}
          accent={rateColor(o.cancelRate)}
          isMobile={isMobile}
        />
        <KpiCard
          label="Median survival"
          value={fmtDur(o.medianSurvivalMinutes)}
          sub={o.survivalSamples
            ? `${fmtDur(o.p25SurvivalMinutes)}–${fmtDur(o.p75SurvivalMinutes)} middle half`
            : 'nothing cancelled yet'}
          accent="#fbbf24"
          isMobile={isMobile}
          title="Order confirmation → first cancellation email"
        />
        <KpiCard
          label="Cancellation emails"
          value={o.cancellationEmails.toLocaleString()}
          sub={`for ${o.cancelled.toLocaleString()} orders · ${o.totalItems.toLocaleString()} items ordered`}
          isMobile={isMobile}
          title="Amazon cancels each item separately, so one order produces several emails"
        />
      </div>

      {/* The gap between "orders" and "cancellation emails" is large enough to
          look like a discrepancy. Explain it once, here, rather than leaving the
          two numbers to contradict each other. */}
      {o.cancellationEmails > o.cancelled && (
        <div style={{
          background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.22)',
          borderRadius: 10, padding: '10px 14px', fontSize: 12, marginBottom: 14,
          color: '#7dd3fc', lineHeight: 1.6,
        }}>
          <strong>{o.cancellationEmails.toLocaleString()} cancellation emails for {o.cancelled.toLocaleString()} orders.</strong>
          {' '}Amazon gives every item in a checkout its own order number and cancels each one separately, so a
          three-item order arrives as three cancellation emails. The counts above are orders; the breakdown below
          is the emails.
          {o.partiallyCancelled > 0 && (
            <> {o.partiallyCancelled.toLocaleString()} order{o.partiallyCancelled === 1 ? ' had' : 's had'} only
            {' '}some items cancelled, so part of {o.partiallyCancelled === 1 ? 'it is' : 'them are'} still live.</>
          )}
        </div>
      )}

      <Timeline series={timeline} isMobile={isMobile} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: isMobile ? 12 : 16, marginBottom: isMobile ? 12 : 16,
      }}>
        <Card title="How long orders survive"
              note={`${o.survivalSamples.toLocaleString()} cancelled`} isMobile={isMobile}>
          <div style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
            {o.survivalSamples === 0 ? (
              <div style={{ fontSize: 12, color: '#475569' }}>Nothing cancelled in this window.</div>
            ) : (
              <>
                {buckets.map(b => (
                  <BarRow key={b.key} label={b.label} count={b.count} max={maxBucket} color={CANCELLED} />
                ))}
                <div style={{ fontSize: 11, color: '#475569', marginTop: 8, lineHeight: 1.5 }}>
                  Fastest {fmtDur(o.fastestSurvivalMinutes)} · slowest {fmtDur(o.slowestSurvivalMinutes)}.
                  {' '}Standing orders have lasted {fmtDur(o.medianStandingAgeMinutes)} so far
                  {o.oldestStandingMinutes != null && <>, the oldest {fmtDur(o.oldestStandingMinutes)}</>}.
                </div>
              </>
            )}
          </div>
        </Card>

        <Card title="Cancellation emails per order"
              note="one per item cancelled" isMobile={isMobile}>
          <div style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
            {cancelEmailsPerCheckout.length === 0 ? (
              <div style={{ fontSize: 12, color: '#475569' }}>Nothing cancelled in this window.</div>
            ) : cancelEmailsPerCheckout.map(c => (
              <BarRow
                key={c.cancellations}
                label={`${c.cancellations} email${c.cancellations === 1 ? '' : 's'}`}
                count={c.checkouts} max={maxPerCk} color={CANCELLED} labelWidth={70}
              />
            ))}
          </div>
        </Card>

        {dimensions.map(d => <DimensionCard key={d.id} dimension={d} isMobile={isMobile} />)}

        <Card title="What is being ordered"
              note="named item only — see note" isMobile={isMobile}>
          <div style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
            {products.length === 0 ? (
              <div style={{ fontSize: 12, color: '#475569' }}>Nothing in this window.</div>
            ) : products.slice(0, 10).map(p => (
              <div key={p.name} style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 52px 56px',
                gap: 10, alignItems: 'center', padding: '5px 0',
              }}>
                <span style={{ fontSize: 12, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>
                  {p.name}
                </span>
                <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${(p.placed / maxProduct) * 100}%`, height: '100%', background: STANDING, borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 12, color: '#e2e8f0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.placed.toLocaleString()}</span>
                <span style={{ fontSize: 11, color: rateColor(p.cancelRate), textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                  {fmtRate(p.cancelRate)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── The orders themselves ───────────────────── */}
      <Card
        title={
          <span style={{ display: 'flex', gap: 4 }}>
            {[['cancelled', `Cancelled (${data.cancellationsTotal.toLocaleString()})`],
              ['standing',  `Standing (${data.standingTotal.toLocaleString()})`]].map(([key, label]) => (
              <button key={key} onClick={() => { setList(key); setShowAll(false); }} style={{
                fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 7, border: 'none',
                cursor: 'pointer', background: list === key ? '#334155' : 'transparent',
                color: list === key ? '#f1f5f9' : '#64748b',
              }}>{label}</button>
            ))}
          </span>
        }
        note={rowsTotal > rows.length ? `newest ${rows.length.toLocaleString()} of ${rowsTotal.toLocaleString()}` : null}
        isMobile={isMobile}
      >
        {!isMobile && rows.length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: rowCols(isMobile), gap: 12,
            padding: '7px 16px', fontSize: 10, color: '#475569', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.5px',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}>
            <span>Product · account</span>
            <span>Placed</span>
            <span>Cancels</span>
            <span>First cancel</span>
            <span style={{ textAlign: 'right' }}>Survived</span>
          </div>
        )}

        {rows.length === 0 ? (
          <div style={{ padding: '28px 0', textAlign: 'center', color: '#475569', fontSize: 13 }}>
            {list === 'standing'
              ? `Nothing placed in the ${winLabel} is still standing.`
              : `Nothing placed in the ${winLabel} was cancelled.`}
          </div>
        ) : shown.map(r => <OrderRow key={r.uid} row={r} isMobile={isMobile} />)}

        {rows.length > 25 && (
          <button onClick={() => setShowAll(v => !v)} style={{
            width: '100%', background: 'rgba(255,255,255,0.03)', border: 'none',
            borderTop: '1px solid rgba(255,255,255,0.06)', color: '#64748b',
            fontSize: 11, fontWeight: 600, padding: '9px 0', cursor: 'pointer',
          }}>
            {showAll ? 'Show fewer' : `Show ${rows.length.toLocaleString()}`}
          </button>
        )}
      </Card>

      <p style={{ fontSize: 11, color: '#475569', marginTop: 14, lineHeight: 1.5 }}>
        One row is one <strong style={{ color: '#94a3b8' }}>checkout</strong> — one "Ordered:" confirmation email —
        because Amazon splits a checkout into an order number per item and cancels each separately. Counting
        cancellation emails as orders would roughly double every figure here.
        {' '}Survival runs from the confirmation email to the first cancellation email; for a standing order it is
        how long it has lasted <em>so far</em>, shown with a <strong style={{ color: '#94a3b8' }}>+</strong>, and it
        can still be cancelled. A standing order younger than the median survival has simply not reached the point
        where cancellations happen, which is why "past the usual cancel time" is the number worth watching.
        {o.linkageExact === o.placed && o.placed > 0 ? (
          <> Product details and order numbers are read from the emails themselves, so each cancellation is
          tied to its order exactly.</>
        ) : (
          <>
            {' '}Product names come from the confirmation subject, which names only the first item and truncates
            it — the rest of the basket, ASINs and prices sit in the email body and are not read.
            {' '}Orders are matched to cancellations by account and recency, since the confirmation subject
            carries no order number.
            {o.linkageExact > 0 && (
              <> {o.linkageExact.toLocaleString()} of {o.placed.toLocaleString()} have since been read from the
              body and are matched exactly.</>
            )}
          </>
        )}
        {' '}History is kept for {data.retentionDays} days.
      </p>
    </div>
  );
}
